import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { getErrorMessage } from '@digilize/shared/utils/helpers';
import { TranslocoService } from '@ngneat/transloco';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { TokenRepository } from '../../auth/infrastructure/token.repository';

import { CheckoutState } from '../states/checkout.state';

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

export interface PaulyApiError {
  type: string;
  message?: string;
  info: PaulyApiErrorInfo | PaulyApiErrorInfo[];
  request_id?: string;
}

export interface PaulyApiErrorInfo {
  field: string;
  kind: PaulyErrorKind;
  message: string; // detailed message should be used if provided
}

export enum PaulyErrorKind {
  MOVIE_NOT_FOUND = 'movie_show_not_found',
  UNKNOWN_ERROR = 'unknown_error',
  GIFT_CARD_EXPIRED = 'gift_card_expired',
  GIFT_CARD_OPERATION_NOT_FOUND = 'gift_card_operation_not_found',
  GIFT_CARD_OPERATION_REMOVAL_NOT_ALLOWED = 'gift_card_operation_removal_not_allowed',
  GIFT_CARD_ALREADY_ADDED_TO_CART = 'gift_card_already_added_to_cart',
  GIFT_CARD_EMPTY = 'gift_card_empty',
  GIFT_CARD_NOT_FOUND = 'gift_card_not_found',
  GIFT_CARD_USED = 'reservation_customer_has_reservation_for_same_event',
  DISCOUNT_NOT_FOUND = 'discount_not_found',
  SEAT_GAP_NOT_ALLOWED = 'seat_gap_not_allowed',
  SEAT_RESERVATION_NOT_EQUAL_TO_AMOUNT_OF_TICKET_PRODUCTS = 'seat_reservation_not_equal_to_amount_of_ticket_products',
}

export const PAULY_EXCEPTIONAL_ERROR_KINDS: PaulyErrorKind[] = [
  PaulyErrorKind.GIFT_CARD_EXPIRED,
  PaulyErrorKind.GIFT_CARD_OPERATION_NOT_FOUND,
  PaulyErrorKind.GIFT_CARD_OPERATION_REMOVAL_NOT_ALLOWED,
  PaulyErrorKind.GIFT_CARD_ALREADY_ADDED_TO_CART,
  PaulyErrorKind.GIFT_CARD_EMPTY,
  PaulyErrorKind.GIFT_CARD_NOT_FOUND,
  PaulyErrorKind.GIFT_CARD_USED,
  PaulyErrorKind.DISCOUNT_NOT_FOUND,
  PaulyErrorKind.SEAT_GAP_NOT_ALLOWED,
  PaulyErrorKind.SEAT_RESERVATION_NOT_EQUAL_TO_AMOUNT_OF_TICKET_PRODUCTS,
];

/**
 * Subscription rules the shop already answers for itself. The information step prints a refused
 * card under the field it was typed into (`refuseSubCard`) or in its own "could not link" bar, and
 * the card dropdown's read of the account swallows a subscription it cannot use — a pass of the
 * buyer's that is paused or expired is nothing they just did, and its cards are simply left out of
 * the list. The global snackbar on top of that says the same thing twice at best, and at worst
 * raises a checkout error over a step the buyer is standing quietly on, before they have picked
 * anything at all (PAUL-3529).
 *
 * `card_required` is deliberately absent: it is what `POST /orders` refuses a line with no card
 * with, the backstop behind the disabled Continue button, and nothing else would say it.
 */
export const SUBSCRIPTION_SILENT_ERROR_KINDS: string[] = [
  'subscription_card_not_found',
  'subscription_card_blocked',
  'subscription_card_already_connected',
  'subscription_card_already_used_on_show',
  'subscription_not_active',
  'subscription_instance_not_active',
  'subscription_instance_already_expired',
  'subscription_product_not_linked',
  'subscription_validity_not_started',
  'subscription_validity_expired',
  'subscription_max_tickets_exceeded',
  'subscription_limit_per_show_exceeded',
  'subscription_min_duration_between_shows',
  'subscription_max_reservations_exceeded',
  'subscription_connect_rate_limited',
  'subscription_connect_user_blocked',
];

/** Every kind the global snackbar stays out of, whichever list it was named on. */
const SILENT_ERROR_KINDS: string[] = [...PAULY_EXCEPTIONAL_ERROR_KINDS, ...SUBSCRIPTION_SILENT_ERROR_KINDS];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private checkoutState: CheckoutState,
    private translocoService: TranslocoService,
    private tokenRepository: TokenRepository
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((httpError: HttpErrorResponse) => {
        if (req.context.get(SKIP_ERROR_INTERCEPTOR)) {
          return throwError(() => httpError);
        }

        if (httpError.status === 401) {
          this.tokenRepository.clear();
          this.checkoutState.setError(this.translocoService.translate('errors.something_went_wrong'));
          return EMPTY;
        }

        const apiError: PaulyApiError = httpError?.error?.error;
        if (httpError.status === 400 && !this.isException(apiError?.info)) {
          this.checkoutState.setError(getErrorMessage(apiError, (key) => this.translocoService.translate(key)));
        }
        return throwError(() => httpError);
      })
    );
  }

  isException(info: PaulyApiErrorInfo | PaulyApiErrorInfo[] | PaulyErrorKind): boolean {
    const resolved = Array.isArray(info) ? info[0] : info;
    if (this.isPaulyErrorKind(resolved)) {
      return SILENT_ERROR_KINDS.includes(resolved);
    } else if (this.isInfoObject(resolved)) {
      return SILENT_ERROR_KINDS.includes(resolved.kind);
    }
    return false;
  }

  isPaulyErrorKind(info: PaulyApiErrorInfo | PaulyErrorKind): info is PaulyErrorKind {
    return typeof info === 'string';
  }

  isInfoObject(info: PaulyApiErrorInfo | PaulyErrorKind): info is PaulyApiErrorInfo {
    return typeof info === 'object' && info !== null && 'kind' in info;
  }
}
